/** Те же контурные значки, что в исходном шаблоне (ic() прототипа); данные сервера не становятся SVG-разметкой:
    неизвестный ключ даёт звёздочку по умолчанию, произвольный путь из ответа не рисуется.
    Фигура задаётся тем же элементом, что в прототипе (BotCore.SV): строка — <path d>, {circle} — <circle>, {rect} — <rect>;
    круги и прямоугольники прототип никогда не пишет путями, поэтому и здесь они остаются своими элементами.
    Источники (порядок разрешения значка у студии — BotStudio: свои значки → BotCore.IC → общий набор SteosUI.icons):
    vendor/widgets-shared/steos-ui.js (icons), assets/bot-core.js (IC), assets/bot-studio.js (свои: swap, dance, clap, arrow, live),
    assets/bot-ref.js (IMGP — «Загрузить картинку»), assets/bot-voice.js (denoise), assets/bot-onb.js (кружок с «плеем»).
    Пути и размеры перенесены дословно, включая регистр команд пути. */
type Fig = string | { circle: [cx: number, cy: number, r: number] } | { rect: [x: number, y: number, w: number, h: number, rx: number] };
const FIGS: Record<string, Fig[]> = {
  /* ── общие элементы интерфейса ── */
  back: ['m15 6-6 6 6 6'],
  chev: ['m9 6 6 6-6 6'],
  chevD: ['m6 9 6 6 6-6'],
  arrow: ['M5 12h14', 'm13 6 6 6-6 6'],
  close: ['M6 6l12 12M18 6 6 18'],
  check: ['m5 12 5 5L20 7'],
  plus: ['M12 5v14M5 12h14'],
  search: [{ circle: [11, 11, 7] }, 'm20 20-3.5-3.5'],
  sort: ['M7 4v16m0 0-3-3m3 3 3-3M17 20V4m0 0-3 3m3-3 3 3'],
  edit: ['M4 20h4l10-10-4-4L4 16zM13 7l4 4'],
  list: ['M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01'],
  /* «Без стиля» в ленте стилей — ban прототипа */
  none: [{ circle: [12, 12, 10] }, 'm4.9 4.9 14.2 14.2'],
  /* ── разделы и инструменты студии ── */
  send: ['M21 3 10 14M21 3l-7 18-4-7-7-4z'],
  clap: [
    'M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z',
    'm6.2 5.3 3.1 3.9',
    'm12.4 3.4 3 4',
    'M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z',
  ],
  camera: ['M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z', { circle: [12, 13, 3] }],
  film: [{ rect: [2, 2, 20, 20, 2.18] }, 'M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5'],
  image: [{ rect: [3, 4, 18, 16, 2] }, { circle: [9, 10, 2] }, 'm21 16-5-5-8 8'],
  video: [{ rect: [3, 6, 13, 12, 2] }, 'm16 10 5-3v10l-5-3'],
  live: [{ rect: [3, 3, 18, 18, 3] }, 'm10 8.5 5 3.5-5 3.5Z'],
  tv: [{ rect: [2, 5, 20, 14, 4] }, 'm10 9 5 3-5 3z'],
  photo: [{ rect: [3, 3, 18, 18, 4] }, { circle: [8, 8, 1.5] }, 'm3 16 5-5 4 4 4-6 5 7'],
  gallery: ['M2 7v10M6 5v14', { rect: [10, 3, 12, 18, 2] }],
  grid: [{ rect: [3, 3, 7, 7, 1.5] }, { rect: [14, 3, 7, 7, 1.5] }, { rect: [3, 14, 7, 7, 1.5] }, { rect: [14, 14, 7, 7, 1.5] }],
  grid4: [{ rect: [3, 3, 7, 7, 1.5] }, { rect: [14, 3, 7, 7, 1.5] }, { rect: [3, 14, 7, 7, 1.5] }, { rect: [14, 14, 7, 7, 1.5] }],
  wand: ['m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z', 'm14 7 3 3M5 6v4M19 14v4M10 2v2M7 8H3M21 16h-4M11 3H9'],
  doc: ['M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z', 'M14 2v4a2 2 0 0 0 2 2h4', 'M10 9H8M16 13H8M16 17H8'],
  text: ['M4 7V4h16v3', 'M9 20h6', 'M12 4v16'],
  scan: ['M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 8h8M7 12h10M7 16h6'],
  mic: [{ rect: [9, 3, 6, 11, 3] }, 'M5 11a7 7 0 0 0 14 0M12 18v3'],
  wave: ['M4 10v4M8 6v12M12 3v18M16 7v10M20 10v4'],
  voice: [{ rect: [9, 3, 6, 12, 3] }, 'M5 11a7 7 0 0 0 14 0M12 18v3m-4 0h8'],
  quiet: ['M11 5 6 9H3v6h3l5 4zM22 9l-6 6m0-6 6 6'],
  headset: ['M4 14v-3a8 8 0 0 1 16 0v3', { rect: [3, 14, 4, 6, 1.5] }, { rect: [17, 14, 4, 6, 1.5] }],
  music: ['M9 18V5l12-2v13', { circle: [6, 18, 3] }, { circle: [18, 16, 3] }],
  box: ['M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z', 'M12 22V12', 'm3.3 7 8.7 5 8.7-5', 'm7.5 4.27 9 5.15'],
  bag: ['M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z', 'M3 6h18', 'M16 10a4 4 0 0 1-8 0'],
  chart: ['M4 20V10m6 10V4m6 16v-7m4 7H2'],
  sliders: ['M4 7h9M19 7h1M4 17h1M11 17h9', { circle: [16, 7, 3] }, { circle: [8, 17, 3] }],
  user: [{ circle: [12, 8, 4] }, 'M4 21a8 8 0 0 1 16 0'],
  users: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', { circle: [9, 7, 4] }, 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  face: ['M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2', 'M8 14s1.5 2 4 2 4-2 4-2', 'M9 9h.01M15 9h.01'],
  dance: [{ circle: [12, 5, 1] }, 'm9 20 3-6 3 6', 'm6 8 6 2 6-2', 'M12 10v4'],
  swap: ['m2 9 3-3 3 3', 'M13 18H7a2 2 0 0 1-2-2V6', 'm22 15-3 3-3-3', 'M11 6h6a2 2 0 0 1 2 2v10'],
  circle: [{ circle: [12, 12, 10] }, 'm10 8 6 4-6 4z'],
  chat: ['M7.9 20A9 9 0 1 0 4 16.1L2 22Z'],
  gift: [
    { rect: [3, 8, 18, 4, 1] },
    'M12 8v13',
    'M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7',
    'M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5',
  ],
  eye: ['M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z', { circle: [12, 12, 3] }],
  sun: [{ circle: [12, 12, 4] }, 'M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M4.9 19.1l1.4-1.4m11.4-11.4 1.4-1.4'],
  globe: [{ circle: [12, 12, 9] }, 'M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18'],
  heart: ['M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z'],
  flame: ['M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z'],
  bolt: ['M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z'],
  star: ['m12 3 2.7 5.6 6.1.8-4.5 4.3 1.1 6.1L12 16.9 6.6 19.8l1.1-6.1L3.2 9.4l6.1-.8z'],
  biz: [{ rect: [3, 7, 18, 14, 3] }, 'M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12a24 24 0 0 0 18 0m-9 0v3'],
  denoise: ['M3 10v4M7 7v10M11 4v16M15 8v8', 'm19.5 3 .8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8z'],
  photoPlus: [
    'M16 5h6M19 2v6',
    'M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5',
    'm21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21',
    { circle: [9, 9, 2] },
  ],
  sparkle: ['M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z'],
};
/** Коды разделов и прежние имена из ответов сервера — на те же значки */
const ALIAS: Record<string, string> = { reels: 'film', slides: 'chart', post: 'gallery', avatar: 'face', clip: 'film', people: 'face' };
/** Толщина штриха у всех значков прототипа — 2; у галочки набора (SteosUI.icons.check) — 2.5 */
const WIDE: Record<string, number> = { check: 2.5 };
/** Размер значка в пикселях, как в разметке прототипа; правила CSS, если они есть, имеют приоритет */
const SIZE: Record<string, number> = {
  back: 18,
  check: 26,
  chev: 16,
  close: 18,
  edit: 14,
  eye: 15,
  globe: 14,
  grid: 13,
  image: 18,
  mic: 18,
  play: 12,
  plus: 16,
  search: 15,
  send: 15,
  sort: 16,
  star: 14,
  sun: 13,
  wave: 16,
};

/** Один элемент значка — тем же тегом, что в прототипе */
function figure(fig: Fig, key: number) {
  if (typeof fig === 'string') return <path key={key} d={fig} />;
  if ('circle' in fig) {
    const [cx, cy, r] = fig.circle;
    return <circle key={key} cx={cx} cy={cy} r={r} />;
  }
  const [x, y, width, height, rx] = fig.rect;
  return <rect key={key} x={x} y={y} width={width} height={height} rx={rx} />;
}

export function StudioIcon({ kind = 'sparkle' }: { kind?: string }) {
  if (kind === 'play')
    return (
      <svg viewBox="0 0 24 24" width={SIZE.play} height={SIZE.play} aria-hidden="true">
        <path d="m8 5 11 7-11 7Z" fill="currentColor" />
      </svg>
    );
  /* имя значка приходит и из данных сервера (§6.3 need.rows[].icon): только свои ключи таблиц — «constructor», «__proto__»
     и прочие имена прототипа объекта не значки, у них тоже звезда */
  const key = Object.hasOwn(FIGS, kind) ? kind : Object.hasOwn(ALIAS, kind) ? ALIAS[kind] : 'sparkle';
  const size = Object.hasOwn(SIZE, key) ? SIZE[key] : 16;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={Object.hasOwn(WIDE, key) ? WIDE[key] : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {FIGS[key].map(figure)}
    </svg>
  );
}
